/* eslint-disable complexity */
import { isNil } from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import AddCommentBox from 'react-scale/components/scalers/tasks/ThreadedInteraction/feedback/AddCommentBox';
import ViewEditCommentBox from 'react-scale/components/scalers/tasks/ThreadedInteraction/feedback/ViewEditCommentBox';
import { useWorkerCommentsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { StepCard } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepCard';
import uuid from 'uuid';
import { Button, Tooltip, Icon } from '@scale/scaleui';
import { ReviewLevel } from '@scale/shared/defaults';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { canAddWorkerCommentsForStep } from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils';
import { useScrollToFeedbackCard } from 'react-scale/remotasks.com/components/Expert/Feedback/SidebarFeedbackCard';

type CommentButtonProps = {
  childId: string;
  turnIndex?: number;
  visible: boolean;
};

export const CommentButton = ({ childId, turnIndex, visible }: CommentButtonProps) => {
  const taskContext = useContext(ChatTaskContext);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const feedbackCenterMode = !!taskContext.feedbackCenterMode;
  const areCommentsReadOnly: boolean = !!taskContext.areCommentsReadOnly;

  const selectedWorkerComment = useWorkerCommentsStore(state => state.selectedWorkerComment);
  const selectWorkerComment = useWorkerCommentsStore(state => state.selectWorkerComment);

  // This comment is used to check if any comment for the step exists at all
  const allWorkerComments = useWorkerCommentsStore(state =>
    state.getAllWorkerCommentsByChildId(childId),
  );
  const visibleComments = allWorkerComments.filter(comment => isNil(comment.feedbackResponse));

  // We manage the comment text state here to persist the text content across re-mounts
  const [addCommentText, setAddCommentText] = useState<string>('');

  // Determine if the worker can add comments
  const isReviewer = reviewLevel !== ReviewLevel.Attempt;
  const canAddComments = canAddWorkerCommentsForStep(
    reviewLevel,
    allWorkerComments,
    taskContext.workerId,
  );
  const hasVisibleComments = visibleComments.length !== 0;

  const [showAddCommentButton, setShowAddCommentButton] = useState<boolean>(hasVisibleComments);

  // Update the showAddCommentButton state when the visibleComments changes
  // This is necessary for when visibleComments goes from 1 to 0, as the
  // add comment button will still be shown
  useEffect(() => {
    setShowAddCommentButton(visibleComments.length > 0);
  }, [visibleComments.length]);

  const [isAddCommentPopperOpen, setIsAddCommentPopperOpen] = useState<boolean>(false);

  // The poppers should open if a comment on this step is selected
  const selectedCommentIndex = useWorkerCommentsStore(state =>
    visibleComments.findIndex(comment => comment.workerCommentId === state.selectedWorkerComment),
  );

  // Determines if the add comment popper can be shown. User can add a comment if
  // the current step is selected, or there are no comments at this step at all.
  const showAddCommentPopper =
    !areCommentsReadOnly &&
    canAddComments &&
    (visibleComments.length === 0 || selectedCommentIndex !== -1);

  const onClickFeedbackCenter = useScrollToFeedbackCard();

  // Return null if comments are not enabled or if the button should not be visible
  if (
    !visible ||
    (!isReviewer && !hasVisibleComments) ||
    (feedbackCenterMode && visibleComments.length === 0)
  )
    return null;

  // Determine relevant props based on which popper is being used
  const showViewEditCommentPopper = visibleComments?.length > 0;
  const isViewEditCommentPopperOpen = selectedCommentIndex !== -1;

  const commentIcon = isReviewer && !hasVisibleComments ? 'comment-plus' : 'comment-dots';
  const toolTipText = !isReviewer
    ? 'View Comment'
    : !hasVisibleComments
    ? 'Add Comment'
    : 'View/Add Comment';

  const isPopoverOpen =
    !feedbackCenterMode &&
    ((showAddCommentPopper && isAddCommentPopperOpen) ||
      (showViewEditCommentPopper && isViewEditCommentPopperOpen));

  // It is guaranteed that there is at least one visible comment in feedback center mode
  // because the option to resolve a comment is disabled
  const onPopoverClickFeedbackCenter = () => {
    selectWorkerComment(visibleComments[0].workerCommentId);
    onClickFeedbackCenter(visibleComments[0].workerCommentId);
  };

  const onPopoverClickNonFeedbackCenter =
    !hasVisibleComments && showAddCommentPopper
      ? () => {
          setIsAddCommentPopperOpen(!isAddCommentPopperOpen);
        }
      : () => {
          setIsAddCommentPopperOpen(!isAddCommentPopperOpen);
          if (selectedWorkerComment === visibleComments[0].workerCommentId!) {
            selectWorkerComment(null);
            return;
          }
          selectWorkerComment(visibleComments[0].workerCommentId!);
        };

  const onPopoverClick = feedbackCenterMode
    ? onPopoverClickFeedbackCenter
    : onPopoverClickNonFeedbackCenter;

  const popoverBadge = showViewEditCommentPopper ? visibleComments.length : undefined;

  return (
    <Tooltip
      text={toolTipText}
      delayDuration={0}
      asChild={false}
      side="bottom"
      target={
        <StepCard.Popover
          icon={commentIcon}
          placement="right-start"
          modifiers={[
            {
              name: 'preventOverflow',
              options: {
                mainAxis: false,
              },
            },
            {
              name: 'offset',
              options: {
                offset: [0, 11],
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top-end', 'bottom-end'],
              },
            },
          ]}
          isOpen={isPopoverOpen}
          onClick={onPopoverClick}
          className="p-0 z-30"
          badge={popoverBadge}
          useStepCardReference
        >
          {/* @ts-expect-error @types/react@18 $FIXME */}
          {({ referenceElement, panelReference, close }) => (
            <div className="flex flex-col p-2 space-y-2">
              {showViewEditCommentPopper && (
                <ViewEditCommentForm
                  panelReference={panelReference}
                  referenceElement={referenceElement}
                  childId={childId}
                />
              )}
              {showAddCommentPopper && showAddCommentButton && (
                <div className="flex justify-end">
                  <Button
                    variant="secondary"
                    className="border border-primary-100 w-8"
                    onClick={() => {
                      setShowAddCommentButton(false);
                    }}
                  >
                    <Icon icon={['fas', 'plus']} size="sm" variant="primary" />
                  </Button>
                </div>
              )}
              {showAddCommentPopper && !showAddCommentButton && (
                <AddCommentForm
                  commentText={addCommentText}
                  setCommentText={setAddCommentText}
                  setOpen={setIsAddCommentPopperOpen}
                  childId={childId}
                  turnIndex={turnIndex}
                  onClose={() => {
                    // If there are visible comments, we want to show the add comment button
                    // else we want to close the popover entirely.
                    if (hasVisibleComments) {
                      setShowAddCommentButton(true);
                    } else {
                      close();
                    }
                  }}
                  panelReference={panelReference}
                  referenceElement={referenceElement}
                />
              )}
            </div>
          )}
        </StepCard.Popover>
      }
    />
  );
};

type AddCommentFormProps = {
  // commentText state is managed by the CommentButton component
  commentText: string;
  setCommentText: (commentText: string) => void;
  setOpen: (open: boolean) => void;
  childId: string;
  turnIndex?: number;
  onClose: () => void;
  panelReference: HTMLDivElement | null;
  referenceElement: HTMLElement | null;
};

export const AddCommentForm = ({
  commentText,
  setCommentText,
  setOpen,
  childId,
  turnIndex,
  onClose,
  panelReference,
  referenceElement,
}: AddCommentFormProps) => {
  const addWorkerComment = useWorkerCommentsStore(state => state.addWorkerComment);
  const selectWorkerComment = useWorkerCommentsStore(state => state.selectWorkerComment);
  const taskContext = useContext(ChatTaskContext);

  const discardComment = useCallback(() => {
    setOpen(false);
    onClose();
  }, [setOpen, onClose]);

  const submitComment = useCallback(
    (commentText: string) => {
      if (commentText.length) {
        const workerCommentId = uuid();
        addWorkerComment({
          author: taskContext.workerId,
          workerCommentId,
          comment: commentText,
          childId,
          turn: turnIndex,
        });
        selectWorkerComment(workerCommentId);
        onClose();
      }
      setCommentText('');
      onClose();
    },
    [
      addWorkerComment,
      selectWorkerComment,
      taskContext.workerId,
      childId,
      turnIndex,
      setCommentText,
      onClose,
    ],
  );

  // Close the popper if the user clicks outside of the popper
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (
        panelReference &&
        !panelReference.contains(event.target) &&
        !panelReference.outerHTML.includes(event.target.outerHTML) &&
        !referenceElement?.outerHTML.includes(event.target.outerHTML) &&
        !referenceElement?.contains(event.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setOpen, referenceElement, panelReference]);

  return (
    <AddCommentBox
      commentText={commentText}
      setCommentText={setCommentText}
      submitComment={submitComment}
      discardComment={discardComment}
    />
  );
};

const ViewEditCommentForm = ({
  childId,
  panelReference,
  referenceElement,
}: {
  childId: string;
  panelReference: HTMLDivElement | null;
  referenceElement: HTMLElement | null;
}) => {
  const taskContext = useContext(ChatTaskContext);
  const areCommentsReadOnly = !!taskContext.areCommentsReadOnly;

  const getAllWorkerCommentsByChildId = useWorkerCommentsStore(
    state => state.getAllWorkerCommentsByChildId,
  );
  const selectWorkerComment = useWorkerCommentsStore(state => state.selectWorkerComment);

  // All the unresolved comments for this step
  const visibleComments = getAllWorkerCommentsByChildId(childId).filter(comment =>
    isNil(comment.feedbackResponse),
  );

  // Find the selected inline worker comment for this step
  const selectedWorkerComment = useWorkerCommentsStore(state => state.selectedWorkerComment);
  const selectedCommentIndex = visibleComments.findIndex(
    comment => comment.workerCommentId === selectedWorkerComment,
  );
  const workerComment =
    selectedCommentIndex !== -1 ? visibleComments.at(selectedCommentIndex) : undefined;

  const canEdit = workerComment?.author === taskContext.workerId;

  // Close the popper if the user clicks outside of the popper
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (
        panelReference &&
        !panelReference.contains(event.target) &&
        !panelReference.outerHTML.includes(event.target.outerHTML) &&
        !referenceElement?.outerHTML.includes(event.target.outerHTML) &&
        !referenceElement?.contains(event.target)
      ) {
        selectWorkerComment(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectWorkerComment, referenceElement, panelReference]);

  // Return null if the selected comment is not for this step or there are no unresolved comments
  if (!workerComment || !visibleComments.length) return null;

  return (
    <ViewEditCommentBox
      workerComment={workerComment}
      selectedCommentIndex={selectedCommentIndex}
      unresolvedComments={visibleComments}
      canEdit={canEdit}
      readOnly={areCommentsReadOnly}
    />
  );
};
